// The fields a presale project's writable levels take, shared by the forms that
// create one: NewPresaleForm (a whole project, PRESALE_FIELDS) and
// ProjectCollectionForm (a building or a component added to one that exists,
// PTC_FIELDS / UTC_FIELDS).
//
// Keys are the Drupal machine names the endpoints accept, so nothing is
// translated on the way out, and each list is a subset of the server's own
// allowlist for that level (_planning_allow('presale' | 'ptc' | 'utc')).
// `options` names a list loaded from the taxonomy endpoints — see
// ProjectStore.fetchProjectTermOptions() — and makes the field a select;
// everything else renders as a plain input.
//
// PTC_FIELDS and UTC_FIELDS are kept here rather than inside
// ProjectCollectionForm because a building added from the project page and a
// building added any other way are the same record, and a field that exists in
// one place and not the other reads as data loss to whoever finds the gap later.

// The presale_project node's own fields — the only level NewPresaleForm writes.
// title, field_address and field_city_neighbourhood are deliberately absent:
// the form asks for those three directly, above the collapsible section, and
// sends them at the top level of the payload where api/presale_project/create
// takes them.
//
// Types follow how each one is stored. The four lot fields are multi-valued
// text — import.inc splits them on commas and trims each part, so "45%" and
// "132, 148" are values that really occur and a number input would make them
// unenterable. A comma typed into one is stored as part of that single value;
// entering several takes the node form. Prices and coordinates are genuinely
// numeric, and the coordinates carry step="any" — without it a number input
// rejects anything that is not a whole number.
//
// field_land_lot_area and field_specific_use_type are read nowhere in the
// backend module, so their storage is unverified. Both are text boxes, and
// _planning_set() types them from the live field config on the way in; a name
// that no longer exists comes back as an "unknown field" warning the form shows
// rather than a silent drop.
export const PRESALE_FIELDS = [
  { key: "field_land_list_price", label: "Land list price", type: "number" },
  { key: "field_land_lot_area", label: "Land lot area" },
  { key: "field_land_sale_price", label: "Land sale price", type: "number" },
  { key: "field_geo_address", label: "Geo address" },
  { key: "field_lat", label: "Lat", type: "number", step: "any" },
  { key: "field_lng", label: "Lng", type: "number", step: "any" },
  { key: "field_lot_area", label: "Total lot area (sqft)" },
  { key: "field_lot_width_f_", label: "Lot width (F)" },
  { key: "field_lot_depth_f_", label: "Lot depth (F)" },
  { key: "field_specific_use_type", label: "Specific use (commercial/industrial)" },
  { key: "field_lot_coverage", label: "Total lot coverage" },
  { key: "field_community_amenity_contribu", label: "Community amenity contribution" },
  { key: "field_development_cost_charge_dc", label: "Development cost charge (DCC)" },
  { key: "field_development_cost_levies_dc", label: "Development cost levies (DCLs)" },
];

// The PTC fields open for entry: the free-text ones only, for now. The typed
// ones a building also carries — lot size, storeys, building height, lat/lng,
// website link — are deliberately left out of creation and set afterwards; the
// server accepts all of them, so restoring one here is a single line.
//
// Every field left is plain text, and two of them have to be. The importer
// fills field_lot_coverage and the three cost fields by splitting a string on
// commas and trimming each part (import.inc), so they are multi-valued text in
// practice — "45%" and "$1.2M (2021)" are values that really occur, and a
// number input would make them unenterable. A comma typed into one is stored as
// part of that single value; entering several takes the node form.
//
// field_specific_use_type has no options endpoint, so it is a text box. If it
// turns out to be a term reference, _planning_set() resolves the name against
// that field's own vocabulary and warns when no term matches — which the form
// shows — so a typo is reported rather than silently dropped.
export const PTC_FIELDS = [
  { key: "field_project_name", label: "PTC project name" },
  { key: "field_address", label: "Address" },
  { key: "field_lot", label: "Lot" },
  { key: "field_lot_coverage", label: "Lot coverage" },
  { key: "field_specific_use_type", label: "Specific use (commercial/industrial)" },
  { key: "field_community_amenity_contribu", label: "Community amenity contribution" },
  { key: "field_development_cost_charge_dc", label: "Development cost charge (DCC)" },
  { key: "field_development_cost_levies_dc", label: "Development cost levies (DCLs)" },
];

// The UTC fields open for entry, ordered as they read: what the component is,
// which building it sits in, its dimensions, its unit mix, its areas and
// density, then where to find it.
//
// Counts and measurements are number inputs — the app does arithmetic on all of
// them (bed counts roll up into the unit mix, areas into FSR), so a string is
// the wrong thing to store. The decimal ones carry step="any"; without it a
// number input rejects anything that is not a whole number.
//
// No term-reference field is here: tenure, project type and status are set on
// the component afterwards, on the Unit & Building tab. Adding one back means a
// key on `options` and restoring its endpoint in
// ProjectStore.fetchProjectTermOptions().
//
// field_building, like field_specific_use_type, is read nowhere in the backend
// module, so its storage type is unverified — it is a text box, and
// _planning_set() types it from the live field config on the way in.
export const UTC_FIELDS = [
  { key: "field_utc_project_name", label: "UTC project name" },
  { key: "field_utc_address", label: "UTC address" },
  { key: "field_building", label: "Building" },
  { key: "field_building_group_id_", label: "Building (group ID)" },
  { key: "field_group_id", label: "Group ID" },
  { key: "field_storey_s", label: "Storeys", type: "number" },
  { key: "field_building_height_metres_", label: "Building height (metres)", type: "number", step: "any" },
  { key: "field_type_units", label: "Units", type: "number" },
  { key: "field_studio", label: "Studio", type: "number" },
  { key: "field_1_bed", label: "1-bed", type: "number" },
  { key: "field_2_bed", label: "2-bed", type: "number" },
  { key: "field_3_bed", label: "3-bed", type: "number" },
  { key: "field_4_bed", label: "4-bed", type: "number" },
  { key: "field_5_bed", label: "5-bed", type: "number" },
  { key: "field_sqft", label: "Floor area (net)", type: "number", step: "any" },
  { key: "field_floor_area_gross_", label: "Floor area (gross)", type: "number", step: "any" },
  { key: "field_floor_area_total_", label: "Floor area (parkade)", type: "number", step: "any" },
  { key: "field_fsr_far", label: "FSR / FAR", type: "number", step: "any" },
  { key: "field_upa_townhome_", label: "UPA", type: "number", step: "any" },
  { key: "field_parking", label: "Parking", type: "number" },
  { key: "field_website_link", label: "Website link", placeholder: "https://" },
];

// Field keys whose value is a count or a measurement — sent as numbers so a
// numeric field is not written with the string the input hands back.
//
// Every key here is rendered as a number input above, which is what makes the
// conversion safe: the browser will not hand back "1,200", so Number() cannot
// turn a typed value into NaN. Adding a key here without doing the same in the
// field list would reintroduce exactly that.
export const NUMERIC_FIELDS = [
  "field_land_list_price",
  "field_land_sale_price",
  "field_lat",
  "field_lng",
  "field_storey_s",
  "field_building_height_metres_",
  "field_type_units",
  "field_studio",
  "field_1_bed",
  "field_2_bed",
  "field_3_bed",
  "field_4_bed",
  "field_5_bed",
  "field_sqft",
  "field_floor_area_gross_",
  "field_floor_area_total_",
  "field_fsr_far",
  "field_upa_townhome_",
  "field_parking",
];

/** A blank value per field, for a form's initial state and its reset. */
export const emptyFields = (fields) =>
  fields.reduce((state, field) => {
    state[field.key] = "";
    return state;
  }, {});

/**
 * Drop the fields nobody filled in, and type the ones that are numbers.
 *
 * An empty string would be WRITTEN as a value rather than skipped, so a level
 * is created with what was actually entered and nothing else.
 */
export const compactFields = (values) =>
  Object.keys(values || {}).reduce((out, key) => {
    const raw = values[key];
    const value = typeof raw === "string" ? raw.trim() : raw;
    if (value === "" || value === null || value === undefined) return out;
    out[key] = NUMERIC_FIELDS.includes(key) ? Number(value) : value;
    return out;
  }, {});
